'use client'

import {ReactNode, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import s from '../style.module.scss'
import {LanguageCodes} from '@app/i18n/settings'
import {Issue} from './feedback-form/actions/fetch-issues'
import {pauseAndMute, playAndUnmute} from '@app/components/game-player/game-functions'
import dynamic from 'next/dynamic'
import Backdrop from '@app/components/header/sections/desktop/search/backdrop/backdrop'
import {useScrollLock} from '@app/hooks/use-scroll-lock'
import triggerAnalytics from '@gamezop/analytics-sdk/client/trigger-analytics'
import {FeedbackTranslation} from '.'
import {usePlayButton} from '@app/components/gameplayer-page/game-image-and-play-btn/play-btn/play-button-context'
const FeedbackForm = dynamic(() => import('./feedback-form'), {
  loading: () => <div className={s.loader_container} />,
})

type Props = {
  children: ReactNode
  lang: LanguageCodes
  issues: Issue[]
  userEmail: string | null
  gameNameEn: string
  gameCode: string
  translations: FeedbackTranslation
}

const FeedbackButtonWrapper = ({children, lang, issues, userEmail, gameNameEn, gameCode, translations}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const {lockScroll, unlockScroll} = useScrollLock()
  const {gameScreenShown} = usePlayButton()

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const openForm = () => {
    setIsOpen(true)
    pauseAndMute()
    lockScroll()
    triggerAnalytics('feedback-button-clicked', {
      'game-name': gameNameEn,
      'game-code': gameCode,
    }).sendAll()
  }

  const closeForm = () => {
    setIsOpen(false)
    if (gameScreenShown) {
      playAndUnmute()
    }
    unlockScroll()
  }

  const Popup = () => {
    return (
      <div className={s.feedback_form_container}>
        <Backdrop showBackdrop={isOpen} onClick={closeForm} />
        <div className={s.feedback_form}>
          <FeedbackForm
            lang={lang}
            closeForm={closeForm}
            issues={issues}
            setIsFormSubmitted={setIsFormSubmitted}
            isFormSubmitted={isFormSubmitted}
            userEmail={userEmail}
            gameNameEn={gameNameEn}
            gameCode={gameCode}
            translations={translations}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      {!isFormSubmitted && (
        <button className={s.button} onClick={openForm}>
          {children}
        </button>
      )}
      {isOpen && isMounted && createPortal(<Popup />, document.body)}
    </>
  )
}

export default FeedbackButtonWrapper
